import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { PlayerAvatar } from "./PlayerAvatar";
import { PairsExportCard } from "./PairsExportCard";
import { toast } from "sonner";
import { Loader2, Crown, TrendingUp, Download, Share2 } from "lucide-react";

export function PairsView({ playersMap }) {
  const [pairs, setPairs] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    api.get("/pairs").then((r) => setPairs(r.data));
  }, []);

  const renderCanvas = async () => {
    if (!exportRef.current) return null;
    return html2canvas(exportRef.current, { backgroundColor: "#0B0F19", scale: 2, logging: false });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const canvas = await renderCanvas();
      const link = document.createElement("a");
      link.download = `classifica-coppie-american-padel.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const canvas = await renderCanvas();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "classifica-coppie-american-padel.png", { type: "image/png" });
      const canShareFiles =
        typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: "Classifica Coppie — American Padel 2026",
            text: "Le coppie più vincenti del campionato 🎾",
          });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = "classifica-coppie-american-padel.png";
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Immagine salvata (condivisione diretta non disponibile)");
    } catch (e) {
      toast.error("Errore durante la condivisione");
    } finally {
      setExporting(false);
    }
  };

  if (!pairs) {
    return (
      <div className="py-20 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  const medal = ["#F59E0B", "#9CA3AF", "#D97706"];

  return (
    <div className="space-y-5" data-testid="pairs-view">
      <PairsExportCard ref={exportRef} pairs={pairs} playersMap={playersMap} />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            Classifica Compagni
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white">
            Coppie Più Vincenti
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-pairs-btn"
            onClick={handleDownload}
            disabled={exporting || !pairs.length}
            size="sm"
            className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 font-semibold uppercase text-xs tracking-wide"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 sm:mr-1.5" />}
            <span className="hidden sm:inline">Scarica</span>
          </Button>
          <Button
            data-testid="share-pairs-btn"
            onClick={handleShare}
            disabled={exporting || !pairs.length}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs tracking-wide"
          >
            <Share2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Condividi</span>
          </Button>
        </div>
      </div>

      {pairs.length === 0 ? (
        <div className="ap-glass rounded-2xl p-8 text-center text-slate-500">
          Nessun risultato ancora registrato. Le coppie appariranno dopo le prime partite.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {pairs.map((p) => {
            const [idA, idB] = p.player_ids;
            const [nameA, nameB] = p.players;
            const podium = p.rank <= 3;
            return (
              <div
                key={p.player_ids.join("-")}
                data-testid={`pair-card-${p.rank}`}
                className="ap-card p-5 relative overflow-hidden"
                style={podium ? { borderColor: medal[p.rank - 1] + "55" } : undefined}
              >
                {podium && (
                  <div
                    className="absolute top-0 right-0 w-20 h-20 -mr-10 -mt-10 rounded-full blur-2xl opacity-30"
                    style={{ background: medal[p.rank - 1] }}
                  />
                )}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {p.rank === 1 ? (
                      <Crown className="w-5 h-5 text-amber-400" fill="currentColor" />
                    ) : (
                      <span
                        className="font-display font-black text-xl"
                        style={{ color: podium ? medal[p.rank - 1] : "#64748b" }}
                      >
                        {p.rank}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                      Coppia
                    </span>
                  </div>
                  <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-lg bg-emerald-500/15 font-mono font-black text-emerald-400">
                    {p.points} pt
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex -space-x-2">
                    <PlayerAvatar id={idA} name={playersMap[idA]?.name || nameA} size={40} />
                    <PlayerAvatar id={idB} name={playersMap[idB]?.name || nameB} size={40} />
                  </div>
                  <div className="min-w-0 leading-tight">
                    <div className="font-bold text-white truncate">{nameA}</div>
                    <div className="font-bold text-white truncate">{nameB}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-slate-950/60 py-2">
                    <div className="font-mono font-black text-lg text-emerald-400">{p.wins}-{p.losses}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">V-P</div>
                  </div>
                  <div className="rounded-xl bg-slate-950/60 py-2">
                    <div className="font-mono font-black text-lg text-white">{p.win_rate}%</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Win</div>
                  </div>
                  <div className="rounded-xl bg-slate-950/60 py-2">
                    <div className="font-mono font-black text-lg text-cyan-400">
                      {p.games_diff > 0 ? "+" : ""}{p.games_diff}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Diff</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="flex items-center gap-2 text-xs text-slate-500">
        <TrendingUp className="w-3.5 h-3.5" />
        Nell'americana ogni coppia gioca insieme una sola volta: la classifica premia le coppie con
        più vittorie, poi più punti e miglior differenza games.
      </p>
    </div>
  );
}
